import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle,
} from '@/components/ui/drawer';
import type { TicketTierDto } from '../api/tickets.api';
import { alegereDeTrimis, modAlegere, optiuniPremiu } from '../utils/optiuni-premiu';

/** Aceeasi limita ca `@Size(max = 500)` pe `TicketRedemptionRequestDTO.alegereProfesor` (backend). */
export const ALEGERE_MAX = 500;

const ID_EROARE = 'alegere-recompensa-eroare';

interface Props {
    tier: TicketTierDto | null;
    available: number;
    busy: boolean;
    /** Eroarea de camp `alegereProfesor` intoarsa de backend (400, cheia `text.prea.lung`),
        mesajul deja in romana — vezi `getFieldErrors` in `response-handler.ts`. */
    eroareAlegere?: string;
    onConfirm: (alegereProfesor: string | undefined) => void;
    onCancel: () => void;
}

/**
 * Confirmarea DINAINTE de cheltuire. Bottom sheet, nu ecran si nu dialog modal clasic: e o
 * DECIZIE, nu o confirmare a ceva deja intamplat. Ecranul e pentru dupa (R12).
 *
 * Cifra pusa in fata e SOLDUL DUPA revendicare, nu costul - costul e deja pe rand, iar intrebarea
 * pe care si-o pune coordonatorul e „cu ce raman?". Un singur CTA primar; anularea e text.
 *
 * Alegerea recompensei (item 15, regula in `optiuni-premiu.ts`): peste o optiune -> radio obligatoriu;
 * exact una -> aleasa automat si afisata; zero -> textarea de dinainte. Butonul NU se dezactiveaza cat
 * lipseste alegerea: un buton mort nu spune de ce e mort. Se poate apasa, iar refuzul apare langa
 * grup, anuntat, cu focus pe prima optiune.
 *
 * Zero `window.confirm`: blocheaza automatizarea de browser si e inutilizabil pe mobil.
 */
export function RedeemSheet({ tier, available, busy, eroareAlegere, onConfirm, onCancel }: Props) {
    const cost = tier?.pointsCost ?? 0;
    const dupa = available - cost;
    const mod = useMemo(() => modAlegere(optiuniPremiu(tier?.descriere)), [tier?.descriere]);

    const [selectata, setSelectata] = useState<string | null>(null);
    const [text, setText] = useState('');
    const [eroareLocala, setEroareLocala] = useState<string | undefined>(undefined);
    const primaOptiune = useRef<HTMLInputElement>(null);

    // Stare goala la fiecare nivel nou ales -- altfel alegerea ramasa de la o revendicare anulata
    // (sheet-ul inchis, apoi redeschis pe alt nivel) ar pleca ATASATA gresit.
    useEffect(() => {
        setSelectata(null);
        setText('');
        setEroareLocala(undefined);
    }, [tier?.id]);

    // Eroarea locala (fara alegere) are prioritate: e cea pe care omul o poate repara acum.
    const eroare = eroareLocala ?? eroareAlegere;

    const trimite = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (busy) return;
        const rezultat = alegereDeTrimis(mod, selectata, text);
        if (!rezultat.ok) {
            setEroareLocala(rezultat.eroare);
            primaOptiune.current?.focus();
            return;
        }
        setEroareLocala(undefined);
        onConfirm(rezultat.alegereProfesor);
    };

    return (
        <Drawer open={tier !== null} onOpenChange={(deschis) => { if (!deschis) onCancel(); }}>
            {/* `max-h` + zona din mijloc derulabila: cu multe optiuni, pe un telefon de 360px, butonul
                „Revendică" trebuie sa ramana la vedere, nu impins sub marginea ecranului. */}
            <DrawerContent className="max-h-[90dvh]">
                {/* `noValidate`: refuzul il arata pagina, langa grup si in romana -- nu balonul
                    nativ al browserului, care dispare singur si nu se poate citi pe mobil. */}
                <form className="mx-auto flex min-h-0 w-full max-w-md flex-col" noValidate onSubmit={trimite}>
                    <DrawerHeader>
                        <DrawerTitle>Revendici „{tier?.name}"?</DrawerTitle>
                        <DrawerDescription className="tabular-nums">
                            {mod.kind === 'text' && tier?.couponValue
                                ? `Voucher de ${tier.couponValue} lei, pentru ${cost} puncte.`
                                : `Costă ${cost} puncte.`}
                        </DrawerDescription>
                    </DrawerHeader>

                    <div className="min-h-0 flex-1 overflow-y-auto px-4">
                        <div className="rounded-lg bg-lilac/20 p-4 text-center">
                            <p className="m-0 text-sm text-muted-foreground">Sold după revendicare</p>
                            <p className="m-0 text-fluid-xl font-bold tabular-nums">{dupa} puncte</p>
                        </div>

                        <div className="mt-4">
                            {mod.kind === 'radio' && (
                                <fieldset
                                    className="m-0 min-w-0 border-0 p-0"
                                    aria-describedby={ID_EROARE}
                                    aria-invalid={!!eroare || undefined}
                                >
                                    <legend className="mb-2 p-0 font-medium">
                                        Alege recompensa <span className="font-normal text-muted-foreground">(obligatoriu)</span>
                                    </legend>
                                    <div className="flex flex-col gap-2">
                                        {mod.optiuni.map((optiune, i) => (
                                            // Tot randul e tinta de atingere (>= 48px), nu doar cercul radio.
                                            <label
                                                key={optiune}
                                                className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border border-hairline px-3 py-2 has-checked:border-magenta has-checked:bg-lilac/20 has-focus-visible:ring-2 has-focus-visible:ring-magenta"
                                            >
                                                <input
                                                    ref={i === 0 ? primaOptiune : undefined}
                                                    type="radio"
                                                    name="alegere-recompensa"
                                                    value={optiune}
                                                    checked={selectata === optiune}
                                                    onChange={() => { setSelectata(optiune); setEroareLocala(undefined); }}
                                                    disabled={busy}
                                                    required
                                                    className="size-5 shrink-0 accent-magenta"
                                                />
                                                <span className="min-w-0 text-sm">{optiune}</span>
                                            </label>
                                        ))}
                                    </div>
                                </fieldset>
                            )}

                            {mod.kind === 'automat' && (
                                // Nu e nimic de ales, dar omul vede ce pleaca in cerere si in emailuri.
                                <div className="rounded-lg border border-hairline p-3">
                                    <p className="m-0 text-small text-muted-foreground">Recompensa</p>
                                    <p className="m-0 font-medium">{mod.optiune}</p>
                                </div>
                            )}

                            {mod.kind === 'text' && (
                                <div className="space-y-2">
                                    <Label htmlFor="alegere-recompensa">Ce recompensă alegi? (opțional)</Label>
                                    <Textarea
                                        id="alegere-recompensa"
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        maxLength={ALEGERE_MAX}
                                        disabled={busy}
                                        aria-invalid={!!eroare}
                                        aria-describedby={`alegere-recompensa-contor ${ID_EROARE}`}
                                        className="min-h-20"
                                    />
                                    <p
                                        id="alegere-recompensa-contor"
                                        aria-live="off"
                                        className="m-0 text-right text-smaller text-gray-600"
                                    >
                                        {text.length}/{ALEGERE_MAX}
                                    </p>
                                </div>
                            )}

                            {/* Aceeasi zona pentru ambele erori: lipsa alegerii (locala) si refuzul
                                serverului (400, `text.prea.lung`). Mereu in DOM, ca `aria-live` sa
                                anunte continutul care apare, nu un nod nou. */}
                            <p
                                id={ID_EROARE}
                                role={eroare ? 'alert' : undefined}
                                aria-live="polite"
                                className="m-0 mt-2 text-smaller text-error"
                            >
                                {eroare}
                            </p>
                        </div>
                    </div>

                    <DrawerFooter>
                        <Button
                            type="submit"
                            className="touch-target min-h-12 w-full bg-magenta hover:bg-magenta-hover"
                            disabled={busy}
                        >
                            <Ticket aria-hidden /> Revendică
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            className="touch-target min-h-12 w-full"
                            disabled={busy}
                            onClick={onCancel}
                        >
                            Renunț
                        </Button>
                    </DrawerFooter>
                </form>
            </DrawerContent>
        </Drawer>
    );
}
